import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../config/api";
import PasswordStrengthInput from "../components/shared/PasswordStrengthInput";
import { validatePassword } from "../lib/passwordRules";

// Reset-password page — landing for the magic link. We validate length
// client-side too so the user gets immediate feedback before the network
// round-trip. The backend re-validates everything.
const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const pwError = validatePassword(next);
    if (pwError) {
      toast.error(pwError);
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: next,
      });
      setDone(true);
      // Bounce to home after a short pause so the user can read the
      // success state. The login form's there too.
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-start justify-center px-4 pt-20" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-8" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 sm:p-8">
          {!done ? (
            <>
              <h1 className="text-2xl font-semibold text-white mb-2">Set a new password</h1>
              <p className="text-gray-400 text-sm mb-6">
                Pick something only you'd know. Minimum 14 characters with a mix of upper, lower, number, and symbol — or use the suggest button below to get a generated one.
              </p>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="rp-new" className="text-gray-300 text-sm">New password</Label>
                  <div className="mt-1">
                    <PasswordStrengthInput
                      value={next}
                      onChange={setNext}
                      placeholder="Pick something strong"
                      testIdBase="rp-new"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="rp-confirm" className="text-gray-300 text-sm">Confirm new password</Label>
                  <Input
                    id="rp-confirm" type="password" autoComplete="new-password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                    placeholder="Re-type it"
                    required
                    data-testid="rp-confirm-input"
                  />
                </div>
                <Button
                  type="submit" disabled={loading || !next || !confirm}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="rp-submit-btn"
                >
                  {loading ? "Updating..." : "Set new password"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="rp-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
              <h1 className="text-xl font-semibold text-white mb-2">Password updated</h1>
              <p className="text-gray-400 text-sm">Redirecting you to sign in…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
